import React from 'react';
import { Control, useWatch } from 'react-hook-form';
import countries from 'world-countries';
import { FormTextarea } from '../components/shared/FormTextarea';
import { TagInput } from '../components/shared/TagInput';
import { RadioGroup } from '../components/shared/RadioGroup';
import { MultiSelect } from '../components/shared/MultiSelect';
import { OnboardingData } from '../types';

interface Step4TargetMarketProps {
  control: Control<OnboardingData>;
}

const Step4TargetMarket: React.FC<Step4TargetMarketProps> = ({ control }) => {
  // Watch for changes in the physical location field
  const hasPhysicalLocation = useWatch({
    control,
    name: 'step4_targetMarket.hasPhysicalLocation',
  });

  const scaleOptions = [
    { value: 'local', label: 'Local (single city)' },
    { value: 'regional', label: 'Multi-city / Regional' },
    { value: 'international', label: 'International' },
    { value: 'global', label: 'Online-only' },
    { value: 'national', label: 'National' },
  ];

  // Generate country options from the world-countries package
  const countriesServedOptions = [
    { value: 'all', label: 'All Countries' }, // Add "All" option at the top
    ...countries
      .map((country) => ({
        value: country.name.common.toLowerCase().replace(/\s+/g, '-'),
        label: country.name.common,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)), // Sort alphabetically
  ];

  return (
    <div className="space-y-4">
      {/* Question 1: Customer personas */}
      <div>
        <label className="block text-sm text-text font-medium mb-3">
          Who is your typical customer? Name and describe 1–3 personas.
        </label>
        <TagInput
          name="step4_targetMarket.customerPersonas"
          control={control}
          placeholder="Add customer persona... (press Enter or comma)"
          rules={{
            validate: (value: string[]) => {
              if (!value || value.length < 1) {
                return 'Please add at least 1 customer persona';
              }
              if (value.length > 3) {
                return 'Please add no more than 3 customer personas';
              }
              return true;
            },
          }}
        />
      </div>
      <FormTextarea
        name="step4_targetMarket.customerPersonasDescription"
        control={control}
        label="Describe these customer personas in more detail"
        placeholder="Describe their demographics, behaviors, needs, and motivations..."
        rows={2}
        rules={{ required: 'This field is required' }}
      />

      {/* Question 2: Pain points */}
      <div>
        <FormTextarea
          name="step4_targetMarket.painPoints"
          control={control}
          label="What are their pain points?"
          placeholder="What challenges or problems do your customers face that your business helps solve..."
          rows={2}
          rules={{ required: 'This field is required' }}
        />
      </div>

      {/* Question 3: Scale and areas */}
      <div className="space-y-6">
        <p className="font-medium">
          What is the scale of your company geographically? What areas do you serve? Where are your
          biggest target markets?
        </p>
        <div>
          <label className="block text-sm text-text font-medium mb-3">1. Scale (choose one)</label>
          <div className="grid md:grid-cols-3 grid-cols-1 gap-4">
            <RadioGroup
              name="step4_targetMarket.scale"
              control={control}
              options={scaleOptions.slice(0, 2)} // Show first two options
              rules={{ required: 'Please select a scale' }}
            />
            <RadioGroup
              name="step4_targetMarket.scale"
              control={control}
              options={scaleOptions.slice(2, 4)} // Show first two options
            />
            <RadioGroup
              name="step4_targetMarket.scale"
              control={control}
              options={scaleOptions.slice(4, 6)} // Show first two options
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-text font-medium mb-3">
            2. Countries you serve (choose all that apply)
          </label>
          <MultiSelect
            name="step4_targetMarket.areasServed"
            control={control}
            options={countriesServedOptions}
            placeholder="Select countries you serve..."
            rules={{
              validate: (value: string[]) => {
                if (!value || value.length < 1) {
                  return 'Please select at least one country you serve';
                }
                return true;
              },
            }}
          />
        </div>
      </div>

      {/* Question 4: Physical location */}
      <div className="space-y-4">
        <p className="font-medium">
          Do you have a physical location? If so, what is the address? Please list all addresses.
        </p>
        <div className="flex gap-6">
          <RadioGroup
            name="step4_targetMarket.hasPhysicalLocation"
            control={control}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            rules={{ required: 'Please select an option' }}
          />
        </div>

        {/* Only show address field if user selected "Yes" */}
        {hasPhysicalLocation === 'yes' && (
          <FormTextarea
            name="step4_targetMarket.physicalAddress"
            control={control}
            label="If so, what is the address? (List all addresses, one per line)"
            placeholder="123 Main St, New York, NY 10001&#10;456 Oak Ave, Los Angeles, CA 90001"
            rows={3}
            rules={{
              required:
                hasPhysicalLocation === 'yes' ? 'Please enter your physical address' : false,
            }}
          />
        )}
      </div>
    </div>
  );
};

export { Step4TargetMarket };
